import { z } from 'zod'

export const FORM_HANDOFF_IDENTITY_MAX_ATTEMPTS = 3

export const formHandoffIdentityStatusSchema = z.enum([
  'NOT_REQUIRED',
  'PENDING',
  'CONFIRMED',
  'DATE_OF_BIRTH_MISSING',
])

export const FORM_HANDOFF_IDENTITY_STATUS = formHandoffIdentityStatusSchema.enum

export type FormHandoffIdentityStatus = z.infer<typeof formHandoffIdentityStatusSchema>

export const formHandoffIdentityMismatchReasonSchema = z.enum([
  'NO_MATCH',
  'NOT_CONFIRMED',
  'DATE_OF_BIRTH_MISSING',
  'DATE_OF_BIRTH_ON_FILE',
])

export const FORM_HANDOFF_IDENTITY_MISMATCH_REASON = formHandoffIdentityMismatchReasonSchema.enum

export type FormHandoffIdentityMismatchReason = z.infer<
  typeof formHandoffIdentityMismatchReasonSchema
>

export const formHandoffIdentitySchema = z.object({
  status: formHandoffIdentityStatusSchema,
  attemptsRemaining: z.number().int(),
})

export type FormHandoffIdentity = z.infer<typeof formHandoffIdentitySchema>

export const confirmDeviceIdentityInputSchema = z.object({
  handoffId: z.uuid(),
  leaseVersion: z.number().int().nonnegative(),
  firstName: z.string().trim().min(1).max(255),
  dateOfBirth: z.iso.date(),
})

export type ConfirmDeviceIdentityInput = z.infer<typeof confirmDeviceIdentityInputSchema>

export const deviceIdentityConfirmationSchema = z.object({
  confirmed: z.literal(true),
  client: z.object({ firstName: z.string().nullable() }),
})

export type DeviceIdentityConfirmation = z.infer<typeof deviceIdentityConfirmationSchema>

export const normalizeIdentityFirstName = (value: string): string =>
  value.trim().normalize('NFC').toLocaleLowerCase('en')

// The date of birth is compared as the yyyy-mm-dd string it is stored as, never through a Date,
// so neither the process nor the site timezone can move it by a day.
export const matchesHandoffIdentity = (
  input: { firstName: string; dateOfBirth: string },
  client: { firstName: string | null; dateOfBirth: string | null }
): boolean =>
  client.firstName !== null &&
  client.dateOfBirth !== null &&
  normalizeIdentityFirstName(input.firstName) === normalizeIdentityFirstName(client.firstName) &&
  input.dateOfBirth === client.dateOfBirth

export const resolveIdentityStatus = (input: {
  required: boolean
  confirmedAt: Date | null
  clientDateOfBirth: string | null
}): FormHandoffIdentityStatus => {
  if (!input.required) {
    return FORM_HANDOFF_IDENTITY_STATUS.NOT_REQUIRED
  }
  if (input.confirmedAt) {
    return FORM_HANDOFF_IDENTITY_STATUS.CONFIRMED
  }
  if (!input.clientDateOfBirth) {
    return FORM_HANDOFF_IDENTITY_STATUS.DATE_OF_BIRTH_MISSING
  }
  return FORM_HANDOFF_IDENTITY_STATUS.PENDING
}

export const resolveIdentityAttemptsRemaining = (failedAttempts: number): number =>
  Math.max(FORM_HANDOFF_IDENTITY_MAX_ATTEMPTS - failedAttempts, 0)
